import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  buscarFrota,
  type FiltrosDashboard,
  type ItemCategoria,
  type RespostaFrota,
} from '../../../../api/contac/contacDashboardApi';
import ComoFunciona, { ComoFuncionaSecao } from '../../../../components/como-funciona/ComoFunciona';
import { notify } from '../../../../shared/feedback/notify';
import {
  corDaSerie,
  corOrdinal,
  formatarMoeda,
  formatarMoedaCompacta,
  formatarNumero,
  TINTA_GRAFICO,
} from '../../../../shared/ui/chart-palette';
import {
  AreaEstado,
  CardGrafico,
  EIXO_LINHA,
  EIXO_TICK,
  ESTILO_TOOLTIP,
  Kpi,
  Legenda,
} from '../../../../shared/ui/GraficoBase';
import '../../../../components/como-funciona/help-circle-btn.css';
import '../../../../shared/feedback/erro-de-tela.css';

const COR_PRINCIPAL = corDaSerie(0);
const COR_ADESOES = corDaSerie(0);
const COR_SAIDAS = corDaSerie(1);

/** Acima disso o gráfico de barras deixa de ser legível; o excedente vira uma linha "Outras". */
const MAX_CATEGORIAS = 8;

export const helpSectionsFrota: ComoFuncionaSecao[] = [
  {
    titulo: 'O que esta aba mostra',
    conteudo: (
      <>
        <p>
          A composição da <strong>carteira de veículos</strong>: quantos estão ativos, quanto de
          valor protegido eles somam, de que categorias e idades são, e quem são os associados por
          trás deles.
        </p>
        <p>
          A contagem sai da mesma fonte que as telas do Contac usam, com a mesma regra de
          normalização de categoria e de situação — se um número aqui divergir de lá, é defeito, não
          diferença de critério.
        </p>
      </>
    ),
  },
  {
    titulo: 'Os números são de agora, não do período',
    conteudo: (
      <>
        <p>
          Os cartões do topo e todos os gráficos de composição descrevem{' '}
          <strong>como o cadastro está hoje</strong>. Mudar o período <strong>não</strong> os altera.
        </p>
        <p>
          O único gráfico que usa o período é <strong>Entradas e saídas por competência</strong>.
          Isso é limite do dado, não escolha: o cadastro guarda a situação atual de cada veículo, não
          a situação que ele tinha em cada mês passado — "veículos ativos em março" é uma pergunta
          que a base não sabe responder.
        </p>
      </>
    ),
  },
  {
    titulo: 'Ativo, inativo e situação desconhecida',
    conteudo: (
      <>
        <p>
          A situação é lida da descrição que vem do SGA. Alguns cadastros{' '}
          <strong>não têm situação preenchida</strong>, e esses aparecem como{' '}
          <strong>situação desconhecida</strong> — em separado, nunca somados aos inativos.
        </p>
        <p>
          É por isso que ativos e inativos podem não fechar o total: a diferença é justamente o que
          está sem informação. Contar o desconhecido como inativo esconderia um problema de cadastro
          em vez de mostrá-lo.
        </p>
      </>
    ),
  },
  {
    titulo: 'Valor protegido',
    conteudo: (
      <p>
        É o valor de proteção registrado para cada veículo, somado apenas sobre os{' '}
        <strong>ativos</strong>. Veículo cujo cadastro não traz esse valor entra na contagem e soma
        zero em valor — por isso a média por veículo pode parecer baixa em recortes com muito
        cadastro incompleto.
      </p>
    ),
  },
  {
    titulo: 'Entradas e saídas por competência',
    conteudo: (
      <>
        <p>
          <strong>Entradas</strong> conta os veículos cuja data de contrato caiu naquele mês;{' '}
          <strong>saídas</strong>, aqueles cuja data de fim de contrato caiu nele. A diferença entre
          as duas linhas é o crescimento líquido do mês.
        </p>
        <p>
          Veículo sem data de fim registrada é tratado como <strong>ainda na carteira</strong> — ele
          nunca aparece como saída, mesmo que esteja inativo por outro motivo.
        </p>
      </>
    ),
  },
  {
    titulo: 'Carteira por consultor',
    conteudo: (
      <p>
        Os <strong>15 maiores</strong> por quantidade de veículos ativos. Veículo sem consultor
        vinculado aparece agrupado em <strong>Sem consultor definido</strong> — é uma linha
        legítima da tabela, não um erro, e costuma indicar cadastro a regularizar na origem.
      </p>
    ),
  },
  {
    titulo: 'Quando a consulta falha',
    conteudo: (
      <p>
        Se os dados não puderem ser carregados, a aba mostra uma faixa vermelha e{' '}
        <strong>não</strong> desenha gráfico zerado. Só trate um gráfico vazio como resposta quando{' '}
        <strong>não houver</strong> aviso de erro na tela.
      </p>
    ),
  },
];

interface Props {
  filtros: FiltrosDashboard;
  /** Alimenta os selects do modal de filtros com o que existe de fato nos dados. */
  onOpcoes?: (opcoes: { bases?: string[]; categorias?: string[] }) => void;
  /** Informa à página o período que o backend efetivamente usou (importa quando não há filtro). */
  onJanela?: (janela: { dataInicio: string; dataFim: string }) => void;
}

const FrotaTab: React.FC<Props> = ({ filtros, onOpcoes, onJanela }) => {
  const [dados, setDados] = useState<RespostaFrota | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const carregar = useCallback(async (f: FiltrosDashboard) => {
    setCarregando(true);
    try {
      const resposta = await buscarFrota(f);
      setDados(resposta);
      setErro(null);
      onOpcoes?.({ categorias: resposta.porCategoria.map((c) => c.categoria) });
      onJanela?.({ dataInicio: resposta.janela.dataInicio, dataFim: resposta.janela.dataFim });
    } catch (e) {
      setDados(null);
      setErro(
        'Não foi possível carregar os dados da frota. Enquanto este aviso estiver na tela, não há ' +
          'número para ler aqui — recarregue a página e, se persistir, acione a TI.',
      );
      notify.error(
        'Não foi possível carregar os dados de frota e associados. Não interprete a tela como ' +
          'carteira vazia enquanto este aviso aparecer.',
        { toastId: 'dashboard-associacao-frota-falhou' },
      );
    } finally {
      setCarregando(false);
    }
    // `onOpcoes` e `onJanela` são estáveis na página (useCallback com atualização funcional);
    // listá-los aqui é correto e não relança a carga.
  }, [onOpcoes, onJanela]);

  useEffect(() => {
    void carregar(filtros);
  }, [carregar, filtros]);

  const respondeu = dados !== null;
  /**
   * Primeira carga: ainda não há número nenhum na tela.
   *
   * ⚠️ Diferente de `carregando` sozinho — numa REconsulta (troca de filtro) os dados anteriores
   * seguem visíveis e esmaecidos por `do-atualizando`. Trocar aquilo por giradores apagaria o que
   * o usuário está lendo a cada ajuste de filtro.
   */
  const primeiraCarga = carregando && !respondeu;
  const textoVazio = respondeu
    ? 'Nenhum veículo encontrado com os filtros selecionados.'
    : 'Não foi possível carregar — veja o aviso acima.';

  /**
   * Categorias acima do teto viram uma linha "Outras".
   *
   * O caminho errado seria gerar mais cores: além de 8 tons, os adjacentes deixam de ser
   * distinguíveis sob daltonismo. Agrupar preserva o total e mantém o gráfico legível.
   */
  const categorias = useMemo(() => {
    const lista = dados?.porCategoria ?? [];
    if (lista.length <= MAX_CATEGORIAS) return lista;

    const principais = lista.slice(0, MAX_CATEGORIAS);
    const resto = lista.slice(MAX_CATEGORIAS);
    const agrupada: ItemCategoria = {
      categoria: `Outras (${resto.length})`,
      quantidade: resto.reduce((soma, c) => soma + c.quantidade, 0),
      quantidadeAtiva: resto.reduce((soma, c) => soma + c.quantidadeAtiva, 0),
      valorProtegido: resto.reduce((soma, c) => soma + c.valorProtegido, 0),
    };
    return [...principais, agrupada];
  }, [dados]);

  const tipos = dados?.porTipo ?? [];
  const idade = dados?.idadeFrota ?? [];
  const etaria = dados?.faixaEtariaAssociados ?? [];
  const crescimento = dados?.crescimentoMensal ?? [];
  const carteira = dados?.carteiraPorConsultor ?? [];

  /**
   * Faixas ordenadas ganham uma rampa de UMA cor (clara → escura), para a ordem ser visível na
   * própria cor. O balde "Não informado" fica fora da ordem e recebe o cinza neutro — pintá-lo com
   * um degrau da rampa o colocaria numa posição da sequência que ele não ocupa.
   */
  const corDaFaixa = (faixa: string, indice: number, total: number): string =>
    faixa.toLowerCase().startsWith('não informado')
      ? TINTA_GRAFICO.neutro
      : corOrdinal(indice, total);

  return (
    <div className={carregando && respondeu ? 'do-atualizando' : undefined}>
      {erro && (
        <div className="erro-de-tela" role="alert">
          {erro}
        </div>
      )}

      <div className="recepcao-header-row">
        <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 20 }}>
          Frota &amp; Associados
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>
      </div>

      <p className="do-nota-janela">
        Os cartões e os gráficos de composição descrevem o cadastro <strong>como ele está hoje</strong> —
        o período selecionado vale apenas para o gráfico de entradas e saídas.
      </p>

      <div className="do-kpis">
        <Kpi
          rotulo="Veículos ativos"
          valor={respondeu ? formatarNumero(dados.kpis.veiculosAtivos) : '-'}
          carregando={primeiraCarga}
          apoio={
            respondeu
              ? `${formatarNumero(dados.kpis.veiculosInativos)} inativos e ${formatarNumero(dados.kpis.veiculosSituacaoDesconhecida)} sem situação`
              : 'Cadastro atual'
          }
        />
        <Kpi
          rotulo="Valor protegido"
          valor={respondeu ? formatarMoedaCompacta(dados.kpis.valorProtegidoAtivo) : '-'}
          carregando={primeiraCarga}
          apoio="Soma sobre os veículos ativos"
        />
        <Kpi
          rotulo="Valor médio por veículo"
          valor={respondeu ? formatarMoeda(dados.kpis.valorProtegidoMedio) : '-'}
          carregando={primeiraCarga}
          apoio="Média entre os ativos com valor informado"
        />
        <Kpi
          rotulo="Associados com veículo ativo"
          valor={respondeu ? formatarNumero(dados.kpis.associadosComVeiculoAtivo) : '-'}
          carregando={primeiraCarga}
          apoio={
            respondeu
              ? `${formatarNumero(dados.kpis.veiculosTotal)} veículos no recorte`
              : 'Cadastro atual'
          }
        />
      </div>

      <div className="do-grid">
        <CardGrafico
          titulo="Veículos ativos por categoria"
          descricao="Categorias normalizadas com a mesma regra das telas do Contac. Acima de oito, o excedente é agrupado em Outras."
        >
          <div className="do-plot-alto">
            {!respondeu || categorias.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={categorias}
                  layout="vertical"
                  margin={{ top: 8, right: 56, left: 8, bottom: 8 }}
                >
                  <CartesianGrid stroke={TINTA_GRAFICO.grade} horizontal={false} />
                  <XAxis type="number" tick={EIXO_TICK} axisLine={false} tickLine={false} />
                  <YAxis
                    type="category"
                    dataKey="categoria"
                    tick={EIXO_TICK}
                    axisLine={false}
                    tickLine={false}
                    width={168}
                  />
                  <Tooltip
                    contentStyle={ESTILO_TOOLTIP}
                    formatter={(valor: any) => [formatarNumero(Number(valor)), 'Veículos ativos']}
                  />
                  <Bar
                    dataKey="quantidadeAtiva"
                    name="Veículos ativos"
                    fill={COR_PRINCIPAL}
                    radius={[0, 4, 4, 0]}
                    maxBarSize={22}
                    label={{
                      position: 'right',
                      fontSize: 12,
                      fill: TINTA_GRAFICO.rotulo,
                      formatter: (v: number) => formatarNumero(v),
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardGrafico>

        <CardGrafico
          titulo="Veículos ativos por tipo"
          descricao="Tipo declarado no cadastro do veículo. Cadastro sem o campo preenchido aparece como não informado."
        >
          <div className="do-plot-alto">
            {!respondeu || tipos.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={tipos}
                  layout="vertical"
                  margin={{ top: 8, right: 56, left: 8, bottom: 8 }}
                >
                  <CartesianGrid stroke={TINTA_GRAFICO.grade} horizontal={false} />
                  <XAxis type="number" tick={EIXO_TICK} axisLine={false} tickLine={false} />
                  <YAxis
                    type="category"
                    dataKey="tipo"
                    tick={EIXO_TICK}
                    axisLine={false}
                    tickLine={false}
                    width={168}
                  />
                  <Tooltip
                    contentStyle={ESTILO_TOOLTIP}
                    formatter={(valor: any) => [formatarNumero(Number(valor)), 'Veículos ativos']}
                  />
                  <Bar
                    dataKey="quantidade"
                    name="Veículos ativos"
                    fill={COR_PRINCIPAL}
                    radius={[0, 4, 4, 0]}
                    maxBarSize={22}
                    label={{
                      position: 'right',
                      fontSize: 12,
                      fill: TINTA_GRAFICO.rotulo,
                      formatter: (v: number) => formatarNumero(v),
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardGrafico>

        <CardGrafico
          titulo="Idade da frota"
          descricao="Faixas pelo ano-modelo do veículo. A cor escurece conforme a faixa avança; cadastro sem ano fica em cinza, fora da sequência."
        >
          <div className="do-plot">
            {!respondeu || idade.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={idade} margin={{ top: 24, right: 12, left: 8, bottom: 8 }}>
                  <CartesianGrid stroke={TINTA_GRAFICO.grade} vertical={false} />
                  <XAxis
                    dataKey="faixa"
                    tick={{ ...EIXO_TICK, fontSize: 11 }}
                    axisLine={EIXO_LINHA}
                    tickLine={false}
                    interval={0}
                  />
                  <YAxis tick={EIXO_TICK} axisLine={false} tickLine={false} width={56} />
                  <Tooltip
                    contentStyle={ESTILO_TOOLTIP}
                    formatter={(valor: any) => [formatarNumero(Number(valor)), 'Veículos']}
                  />
                  <Bar
                    dataKey="quantidade"
                    name="Veículos"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={48}
                    label={{
                      position: 'top',
                      fontSize: 12,
                      fill: TINTA_GRAFICO.rotulo,
                      formatter: (v: number) => formatarNumero(v),
                    }}
                  >
                    {idade.map((item, indice) => (
                      <Cell
                        key={item.faixa}
                        fill={corDaFaixa(item.faixa, indice, idade.length)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardGrafico>

        <CardGrafico
          titulo="Faixa etária dos associados"
          descricao="Associados com ao menos um veículo ativo, agrupados pela idade. Cadastro sem data de nascimento fica em cinza."
        >
          <div className="do-plot">
            {!respondeu || etaria.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={etaria} margin={{ top: 24, right: 12, left: 8, bottom: 8 }}>
                  <CartesianGrid stroke={TINTA_GRAFICO.grade} vertical={false} />
                  <XAxis
                    dataKey="faixa"
                    tick={{ ...EIXO_TICK, fontSize: 11 }}
                    axisLine={EIXO_LINHA}
                    tickLine={false}
                    interval={0}
                  />
                  <YAxis tick={EIXO_TICK} axisLine={false} tickLine={false} width={56} />
                  <Tooltip
                    contentStyle={ESTILO_TOOLTIP}
                    formatter={(valor: any) => [formatarNumero(Number(valor)), 'Associados']}
                  />
                  <Bar
                    dataKey="quantidade"
                    name="Associados"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={48}
                    label={{
                      position: 'top',
                      fontSize: 12,
                      fill: TINTA_GRAFICO.rotulo,
                      formatter: (v: number) => formatarNumero(v),
                    }}
                  >
                    {etaria.map((item, indice) => (
                      <Cell
                        key={item.faixa}
                        fill={corDaFaixa(item.faixa, indice, etaria.length)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardGrafico>
      </div>

      <div className="do-grid-largo">
        <CardGrafico
          titulo="Entradas e saídas por competência"
          descricao="Único gráfico desta aba que responde ao período. Entradas contam veículos pela data de contrato; saídas, pela data de fim de contrato."
        >
          <div className="do-plot-alto">
            {!respondeu || crescimento.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={crescimento} margin={{ top: 12, right: 24, left: 8, bottom: 8 }}>
                  <CartesianGrid stroke={TINTA_GRAFICO.grade} vertical={false} />
                  <XAxis
                    dataKey="competencia"
                    tick={EIXO_TICK}
                    axisLine={EIXO_LINHA}
                    tickLine={false}
                  />
                  <YAxis tick={EIXO_TICK} axisLine={false} tickLine={false} width={56} />
                  <Tooltip
                    contentStyle={ESTILO_TOOLTIP}
                    formatter={(valor: any, nome: any) => [formatarNumero(Number(valor)), nome]}
                  />
                  <Line
                    type="monotone"
                    dataKey="adesoes"
                    name="Entradas"
                    stroke={COR_ADESOES}
                    strokeWidth={2}
                    dot={{ r: 4, strokeWidth: 2, stroke: TINTA_GRAFICO.superficie }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="saidas"
                    name="Saídas"
                    stroke={COR_SAIDAS}
                    strokeWidth={2}
                    dot={{ r: 4, strokeWidth: 2, stroke: TINTA_GRAFICO.superficie }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
          {respondeu && crescimento.length > 0 && (
            <Legenda
              itens={[
                { rotulo: 'Entradas', cor: COR_ADESOES },
                { rotulo: 'Saídas', cor: COR_SAIDAS },
              ]}
            />
          )}
        </CardGrafico>
      </div>

      <div className="do-grid-largo">
        <CardGrafico
          titulo="Carteira por consultor"
          descricao="Os 15 maiores por veículos ativos. Passe o cursor sobre um nome cortado para ver o texto inteiro."
        >
          {!respondeu || carteira.length === 0 ? (
            <div className="do-plot">
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            </div>
          ) : (
            <div className="do-tabela-wrapper">
              <table className="do-tabela">
                <thead>
                  <tr>
                    <th>Consultor</th>
                    <th>Veículos ativos</th>
                    <th>Valor protegido</th>
                  </tr>
                </thead>
                <tbody>
                  {carteira.map((linha) => (
                    <tr key={`${linha.codigoVoluntario}-${linha.consultor}`}>
                      <td title={`${linha.consultor} (código ${linha.codigoVoluntario})`}>
                        {linha.consultor}
                        <span className="do-codigo">{linha.codigoVoluntario}</span>
                      </td>
                      <td className="do-num">{formatarNumero(linha.veiculosAtivos)}</td>
                      <td className="do-num">{formatarMoeda(linha.valorProtegido)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardGrafico>
      </div>

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Frota e Associados"
        secoes={helpSectionsFrota}
      />
    </div>
  );
};

export default FrotaTab;
